import { apiFetch, ApiError } from './client'
import {
  feesReportSchema,
  valueReportSchema,
  topProductsReportSchema,
  type FeesReport,
  type ValueReport,
  type TopProductsReport,
} from '@/lib/schemas/reports'

/**
 * Reports API (Screen 4). Three JSON getters plus an xlsx downloader. Date range
 * params are omitted when blank so the server applies its defaults (fees: current
 * week Mon–Sun; value: current month; top-products: rolling 90 days).
 */

async function readErrorMessage(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.json()) as { detail?: unknown }
    const detail = body?.detail
    if (typeof detail === 'string') return detail
    if (detail && typeof detail === 'object') {
      const message = (detail as Record<string, unknown>).message
      if (typeof message === 'string') return message
    }
  } catch {
    // non-JSON body — fall through to the caller's fallback
  }
  return undefined
}

async function fail(res: Response, fallback: string): Promise<never> {
  const detail = await readErrorMessage(res)
  throw new ApiError(res.status, detail ?? `${fallback} (${res.status})`, detail)
}

export interface DateRangeParams {
  start?: string
  end?: string
}
export interface ValueParams extends DateRangeParams {
  compare?: boolean
}
export type TopSort = 'units' | 'orders' | 'value'
export interface TopProductsParams extends DateRangeParams {
  sort?: TopSort
}

type QueryValue = string | boolean | undefined

/** Build a `?a=b&c=d` string, dropping blank/undefined/false values. */
function buildQuery(entries: Array<[string, QueryValue]>): string {
  const params = new URLSearchParams()
  for (const [key, value] of entries) {
    if (value === undefined || value === '' || value === false) continue
    params.set(key, String(value))
  }
  const s = params.toString()
  return s ? `?${s}` : ''
}

/** GET /api/reports/fees — weekly fee reconciliation vs KH's invoice. */
export async function getFeesReport(params: DateRangeParams): Promise<FeesReport> {
  const path = `/api/reports/fees${buildQuery([
    ['start', params.start],
    ['end', params.end],
  ])}`
  const res = await apiFetch(path, { method: 'GET' })
  if (!res.ok) return fail(res, 'Failed to load fee reconciliation')
  return feesReportSchema.parse(await res.json())
}

/** GET /api/reports/value — value summary, optionally vs the prior period. */
export async function getValueReport(params: ValueParams): Promise<ValueReport> {
  const path = `/api/reports/value${buildQuery([
    ['start', params.start],
    ['end', params.end],
    ['compare', params.compare],
  ])}`
  const res = await apiFetch(path, { method: 'GET' })
  if (!res.ok) return fail(res, 'Failed to load value summary')
  return valueReportSchema.parse(await res.json())
}

/** GET /api/reports/top-products — ranked products with stock signals. */
export async function getTopProducts(params: TopProductsParams): Promise<TopProductsReport> {
  const path = `/api/reports/top-products${buildQuery([
    ['start', params.start],
    ['end', params.end],
    ['sort', params.sort],
  ])}`
  const res = await apiFetch(path, { method: 'GET' })
  if (!res.ok) return fail(res, 'Failed to load top products')
  return topProductsReportSchema.parse(await res.json())
}

export type ReportPanel = 'fees' | 'value' | 'top-products'

/** Parse the download filename from a Content-Disposition header, if present. */
function filenameFromDisposition(header: string | null): string | undefined {
  if (!header) return undefined
  // Prefer RFC 5987 filename*=UTF-8''… then fall back to a quoted filename=.
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/^"|"$/g, '').trim())
    } catch {
      // malformed encoding — fall through to the plain filename
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain?.[1]?.trim()
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * Fetch a panel's Excel export (auth header attached by apiFetch), read the blob,
 * and trigger a browser download. Uses the server's Content-Disposition filename
 * when provided, otherwise a sensible `<panel>-report.xlsx` fallback.
 */
export async function downloadReportXlsx(
  panel: ReportPanel,
  params: DateRangeParams & { sort?: TopSort },
): Promise<void> {
  const path = `/api/reports/${panel}${buildQuery([
    ['start', params.start],
    ['end', params.end],
    ['sort', params.sort],
    ['format', 'xlsx'],
  ])}`
  const res = await apiFetch(path, { method: 'GET' })
  if (!res.ok) return fail(res, 'Export failed')
  const blob = await res.blob()
  const filename = filenameFromDisposition(res.headers.get('content-disposition')) ?? `${panel}-report.xlsx`
  triggerBrowserDownload(blob, filename)
}
